import { z } from 'zod';
import { ConversationChannelSchema } from './chat-channel.js';
// =============================================================================
// Chat buttons - the buttons under an assistant reply (web chat)
// =============================================================================
/**
 * WhatsApp's quick-reply limits (at most three buttons, titles at most 20
 * characters), adopted on web so one button set can render on both channels.
 */
export const MAX_CHAT_BUTTONS = 3;
export const MAX_CHAT_BUTTON_LABEL_CHARS = 20;
/** The longest message a suggestion button sends when tapped. */
export const MAX_SUGGESTION_MESSAGE_CHARS = 500;
/** A button's slot in its set. `index` names it on a press or a tap. */
const ButtonIndexSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
const ButtonLabelSchema = z.string().min(1).max(MAX_CHAT_BUTTON_LABEL_CHARS);
/**
 * A button as a client renders it, by `kind`:
 * - `action` runs one call the server pinned when it offered the button; the client
 *   names the button by its reply's id and its `index` and never sees the call.
 * - `suggestion` pins no call: a tap sends `message` as the user's own turn. The
 *   client shows `message` at once and names the button by `(messageId, index)`;
 *   the server resolves the sent text from its own row.
 *
 * `index` is the button's slot in its set, which stops matching array position once
 * an expired action is left out of a mixed set.
 */
const RenderedActionButtonSchema = z.object({ kind: z.literal('action'), index: ButtonIndexSchema, label: ButtonLabelSchema });
const RenderedSuggestionButtonSchema = z.object({
    kind: z.literal('suggestion'),
    index: ButtonIndexSchema,
    label: ButtonLabelSchema,
    message: z.string().min(1).max(MAX_SUGGESTION_MESSAGE_CHARS),
});
export const RenderedButtonSchema = z.discriminatedUnion('kind', [RenderedActionButtonSchema, RenderedSuggestionButtonSchema]);
/**
 * A set of rendered buttons. A button without an `index` takes its array position.
 *
 * TODO: remove this shim in the release after the one that lands it. It exists for a
 * fresh client talking to a backend revision that has not rolled yet: that backend
 * sends live action sets with no `index` and no expiry filtering, so position equals
 * index there.
 */
export const RenderedButtonsSchema = z
    .array(z.discriminatedUnion('kind', [
    RenderedActionButtonSchema.extend({ index: ButtonIndexSchema.optional() }),
    RenderedSuggestionButtonSchema.extend({ index: ButtonIndexSchema.optional() }),
]))
    .min(1)
    .max(MAX_CHAT_BUTTONS)
    .transform((buttons) => buttons.map((button, position) => ({ ...button, index: button.index ?? position })))
    .pipe(z.array(RenderedButtonSchema));
/**
 * Custom SSE event carrying the buttons for the reply that just finished, sent
 * before RUN_FINISHED. `messageId` is the reply's persisted id, the id history
 * returns it under.
 */
export const CHAT_BUTTONS_EVENT = 'buttons';
export const ChatButtonsEventSchema = z.object({
    type: z.literal(CHAT_BUTTONS_EVENT),
    messageId: z.string(),
    buttons: RenderedButtonsSchema,
});
/**
 * What a press did. Every value except `pending` is a recorded decision: a button
 * set is consumed by its first press, and nothing is ever retried from it.
 *
 * - `executed`: the call ran as the presser and returned.
 * - `stale`: a record the call was pinned to changed first; nothing ran.
 * - `failed`: the route refused the call; nothing changed.
 * - `unknown`: the call did not return (timeout, crash). It may have landed, so
 *   it is never re-run.
 * - `expired`: the set outlived its offer.
 * - `sent`: a suggestion was tapped and its message stored as the user's own turn.
 *   An action tap on such a set finds it decided.
 * - `pending`: another press is running this set's call right now.
 */
export const ButtonOutcomeSchema = z.enum(['executed', 'stale', 'failed', 'unknown', 'expired', 'sent']);
export const ButtonPressOutcomeSchema = z.enum([...ButtonOutcomeSchema.options, 'pending']);
export const ButtonPressParamsSchema = z.object({
    accountId: z.string(),
    conversationId: z.uuid(),
    messageId: z.string().min(1),
    /** Path params arrive as strings; one per button slot. */
    index: z.enum(['0', '1', '2']),
});
/** A tap on a suggestion button, as a suggestion stream request names it: the reply's id and the button's slot. */
export const ChatSuggestionTapSchema = z.object({
    messageId: z.string().min(1),
    index: ButtonIndexSchema,
});
/**
 * A button set as the super-admin conversation view sees it: the pinned call of every
 * action button, the message of every suggestion, and the whole decision trail. Admin only - the calls never reach a user-facing
 * schema. `outcome` is null until the set is decided; `pressedIndex` is null when no
 * press ran (offered, or settled as expired).
 */
export const AdminButtonSetStatusSchema = z.enum(['offered', 'pending', 'decided']);
export const AdminButtonSetSchema = z.object({
    status: AdminButtonSetStatusSchema,
    offeredAt: z.string(),
    claimedAt: z.string().nullable(),
    decidedAt: z.string().nullable(),
    pressedIndex: z.number().int().nullable(),
    outcome: ButtonOutcomeSchema.nullable(),
    receipt: z.string().nullable(),
    buttons: z.array(z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('action'),
            position: z.number().int(),
            label: z.string(),
            tool: z.string(),
            args: z.unknown(),
            receipt: z.string(),
        }),
        z.object({ kind: z.literal('suggestion'), position: z.number().int(), label: z.string(), message: z.string() }),
    ])),
});
/**
 * `metadata` on the human row a press appends: how admin and analytics tell a tap
 * from typing. Written by the press (chat.service.buttons.ts), read by the admin view.
 */
export const ButtonPressMetadataSchema = z.object({
    buttonPress: z.object({
        buttonSetId: z.string(),
        index: z.number().int(),
        /** What the tap was. Absent on rows stored before suggestions existed: those are actions. */
        kind: z.enum(['action', 'suggestion']).optional(),
        /** The channel the press came from. Absent on rows stored before presses recorded it. */
        channel: ConversationChannelSchema.optional(),
    }),
});
